import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { NatService } from 'src/app/services/nat.service';
import { ToastrService } from 'ngx-toastr';
import { ImportWizardStorage, TabularColumns } from '@/app/pages/project/map/repository/import-wizard/import-wizard.storage';
import { ImportWizardTabularService } from '@/app/pages/project/map/repository/import-wizard/components/tabular/import-wizard-tabular.service';
import { SupersetService } from 'src/app/services/superset.service';
import { ProjectService } from 'src/app/services/projects.service';
import { Subscription as NatsSubscription } from 'nats.ws';

export type TabularNatsResponseInterface = {
    status: string;
    rows_validated: number;
    total_rows: number;
    progress_percentage: number;
    errors_found: number;
};

export interface TabularValidationStartOptions {
    importId: string;
    columns: TabularColumns[];
}

@Injectable()
export class ImportWizardTabularValidationHandler implements OnDestroy {

    private readonly _progress$ = new Subject<number>();
    private readonly _subs = new Subscription();
    private _tableName: string | null = null;
    private _isProgressCompleted: boolean = false;
    private _natsSubs: NatsSubscription[] = [];

    readonly progress$: Observable<number> = this._progress$.asObservable();

    constructor(
        private readonly natsService: NatService,
        private readonly importWizardTabularService: ImportWizardTabularService,
        private readonly importWizardStorage: ImportWizardStorage,
        private readonly toastrService: ToastrService,
        private readonly supersetService: SupersetService,
        private readonly projectService: ProjectService
    ) {}

    init(importId: string): void {
        this._subscribeNats(importId);
    }

    /**
     * Define o nome da tabela final para criação do DW.
     * @param tableName Nome da tabela (ex: _uploaded_table_...)
     */
    setTableName(tableName: string): void {
        this._tableName = tableName;
        console.log('[TabularValidationHandler] Table name set to:', tableName);
        
        // Se o progresso já chegou em 100%, cria o DW agora
        if (this._isProgressCompleted) {
            console.log('[TabularValidationHandler] Progress already completed, creating DW now.');
            this._createSupersetDW(tableName);
        }
    }

    /**
     * Subscreve o tópico de progresso da importação final.
     * @param importId ID do job de importação
     */
    subscribeToImportProgress(importId: string): void {
        const topic = `import_wizard.${importId}.progress`;
        this.natsService.subscribeToTopic(topic, (msg: string) => {
            this._handleImportProgressMessage(msg);
        }).then(sub => {
            if (sub) {
                this._natsSubs.push(sub);
            }
        });
    }

     /**
     * Inicia o fluxo completo de validação:
     * 1. Dispara o POST de validação
     * 2. Assina o tópico NATS para acompanhar o progresso
     * 3. Ao chegar em 100%:
     *    - sucesso → setNextStep()
     *    - erro    → busca erros, exibe warning e setNextStep()
     *
     * @param importId  ID do import wizard em andamento
     * @param columns   Colunas a serem salvas no storage
     */
    start({ importId, columns }: TabularValidationStartOptions): void {
        const sub = this.importWizardTabularService
            .postValidateImportWizard(importId)
            .subscribe({
                next: () => {
                    this.importWizardStorage.setDisplayColumns(columns);
                },
                error: (err) => {
                    console.error('[TabularValidationHandler] Erro ao iniciar validação:', err);
                },
            });

        this._subs.add(sub);
    }

    ngOnDestroy(): void {
        this._progress$.complete();
        this._subs.unsubscribe();
        this._natsSubs.forEach(sub => {
            try {
                sub.unsubscribe();
            } catch (err) {
                console.error('[TabularValidationHandler] Erro ao desinscrever NATS:', err);
            }
        });
        this._natsSubs = [];
    }

    private _subscribeNats(importId: string): void {
        const topic = `import_wizard.${importId}.validation`;
        this.natsService.subscribeToTopic(topic, (msg: string) => {
            this._handleNatsMessage(msg);
        }).then(sub => {
            if (sub) {
                this._natsSubs.push(sub);
            }
        });
    }

    private _handleNatsMessage(msg: string): void {
        try {
            const result: TabularNatsResponseInterface = JSON.parse(msg);
            this._progress$.next(result.progress_percentage);

            if (result.progress_percentage === 100) {
                result.errors_found > 0
                    ? this.toastrService.warning('Foram encontrados erros na importação!')
                    : this.toastrService.success('Importação validada com sucesso!');

                this.importWizardStorage.setNextStep();
            }
        } catch (error) {
            console.error('[TabularValidationHandler] Erro ao processar mensagem NATS:', error);
        }
    }

    private _handleImportProgressMessage(msg: string): void {
        try {
            const result = JSON.parse(msg);
            console.log('[TabularValidationHandler] NATS message:', result);
            this._progress$.next(result.progress_percentage);

            if (Number(result.progress_percentage) === 100) {
                console.log('[TabularValidationHandler] Progress 100 reached.');
                this._isProgressCompleted = true;
                
                // Prioritize tableName from result (if backend is updated later), otherwise use stored _tableName
                const tableName = result.table_name || this._tableName;
                if (tableName) {
                    this._createSupersetDW(tableName);
                } else {
                    console.log('[TabularValidationHandler] Table name not available yet, waiting for setTableName.');
                }
            }
        } catch (error) {
            console.error('[TabularValidationHandler] Erro ao processar mensagem NATS de progresso:', error);
        }
    }

    private _createSupersetDW(tableName: string | null): void {
        console.log('[TabularValidationHandler] Attempting to create DW with tableName:', tableName);
        if (this.importWizardStorage.state().isShared) {
            console.log('[TabularValidationHandler] Import is shared, skipping DW creation');
            return;
        }

        this.projectService.projectId$.pipe(take(1)).subscribe(projectId => {
            console.log('[TabularValidationHandler] Project ID:', projectId);
            if (!tableName || !projectId) {
                console.warn('[TabularValidationHandler] Missing tableName or projectId', { tableName, projectId });
                return;
            }

            const finalTableName = tableName.includes('.') ? tableName : `public.${tableName}`;

            const data = {
                project_id: projectId,
                tables: [finalTableName]
            };

            this.supersetService.postCreateSupersetDWS(data).subscribe({
                next: () => {
                    console.log('[TabularValidationHandler] DW criado automaticamente com sucesso');
                },
                error: (err) => {
                    console.error('[TabularValidationHandler] Erro ao criar DW automaticamente:', err);
                },
            });
        });
    }
}
